import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Upload, ShoppingBag, Search, Filter, Plus, RefreshCw, Trash2, Eye, Download,
  CheckCircle2, XCircle, Loader2, Clock, AlertCircle, AlertTriangle,
} from "lucide-react";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { ImportWizard } from "./ImportWizard";
import { useFeatures } from "../lib/features";

const PLATFORM_META = {
  amazon: { label: "Amazon", color: "text-orange-400 bg-orange-500/10 border-orange-500/30" },
  shopify: { label: "Shopify", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  woocommerce: { label: "WooCommerce", color: "text-violet-400 bg-violet-500/10 border-violet-500/30" },
  flipkart: { label: "Flipkart", color: "text-blue-400 bg-blue-500/10 border-blue-500/30" },
  meesho: { label: "Meesho", color: "text-pink-400 bg-pink-500/10 border-pink-500/30" },
  generic: { label: "Generic CSV", color: "text-zinc-300 bg-zinc-700/30 border-zinc-700" },
};

const STATUS_META = {
  pending: { icon: Clock, color: "text-zinc-400 bg-zinc-700/30 border-zinc-700", label: "Pending" },
  uploading: { icon: Loader2, color: "text-blue-400 bg-blue-500/10 border-blue-500/30", label: "Uploading" },
  validating: { icon: Loader2, color: "text-blue-400 bg-blue-500/10 border-blue-500/30", label: "Validating" },
  processing: { icon: Loader2, color: "text-blue-400 bg-blue-500/10 border-blue-500/30", label: "Processing" },
  completed: { icon: CheckCircle2, color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30", label: "Completed" },
  partial: { icon: AlertCircle, color: "text-amber-400 bg-amber-500/10 border-amber-500/30", label: "Partial" },
  failed: { icon: XCircle, color: "text-red-400 bg-red-500/10 border-red-500/30", label: "Failed" },
};

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 transition-colors hover:border-zinc-700" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</p>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
    </div>
  );
}

function fmtNum(n) { return new Intl.NumberFormat("en-IN").format(n || 0); }
function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch { return d; }
}
function fmtDur(ms) {
  if (!ms) return "—";
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.floor(s % 60)}s`;
}

export default function Imports() {
  const [stats, setStats] = useState(null);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("newest");
  const [viewing, setViewing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { import, mode: "record" | "data" }
  const { refresh: refreshFeatures } = useFeatures();

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (platform) params.set("platform", platform);
      if (status) params.set("status", status);
      if (sort) params.set("sort", sort);
      const [s, list] = await Promise.all([
        api.get("/imports/stats"),
        api.get(`/imports?${params.toString()}`),
      ]);
      setStats(s.data); setImports(list.data);
    } catch (e) {
      toast.error("Failed to load imports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [search, platform, status, sort]);

  // Live progress polling for in-flight imports
  useEffect(() => {
    const inflight = imports.some((i) => ["pending", "uploading", "validating", "processing"].includes(i.status));
    if (!inflight) return;
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [imports]);

  const handleDelete = async (id, purge) => {
    try {
      await api.delete(`/imports/${id}${purge ? "?purge=true" : ""}`);
      toast.success(purge ? "Imported data removed" : "Import record deleted");
      // A purge changes org capability + BI + opportunities — refresh feature
      // gates so downstream pages don't render stale states.
      if (purge) {
        try { await refreshFeatures(); } catch { /* non-blocking */ }
      }
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleRetry = async (id) => {
    try {
      await api.post(`/imports/${id}/retry`);
      toast.success("Retry started");
      refresh();
    } catch { toast.error("Retry failed"); }
  };

  const handleDownloadErrors = async (id) => {
    const a = document.createElement("a");
    a.href = `${API_BASE}/imports/${id}/errors.csv`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="imports-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Imports</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
            Universal Commerce Import Engine
          </h1>
          <p className="mt-2 text-zinc-400">Ingest from any platform — normalize into one Milkath model.</p>
        </div>
        <button
          onClick={() => setWizardOpen(true)}
          className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors"
          data-testid="imports-new-btn"
        >
          <Plus className="h-4 w-4" /> Import Data
        </button>
      </div>

      {/* KPI Grid */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
        <StatCard label="Total Imports" value={fmtNum(stats?.total_imports)} testid="kpi-total-imports" />
        <StatCard label="Successful" value={fmtNum(stats?.successful)} sub="completed + partial" testid="kpi-successful" />
        <StatCard label="Failed" value={fmtNum(stats?.failed)} testid="kpi-failed" />
        <StatCard label="Records Processed" value={fmtNum(stats?.processed)} testid="kpi-processed" />
        <StatCard label="Orders Imported" value={fmtNum(stats?.orders)} testid="kpi-orders-imported" />
        <StatCard label="Customers Imported" value={fmtNum(stats?.customers)} testid="kpi-customers-imported" />
        <StatCard label="Products Imported" value={fmtNum(stats?.products)} testid="kpi-products-imported" />
        <StatCard label="Last Import" value={stats?.last_import_at ? new Date(stats.last_import_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }) : "—"} sub={stats?.last_import_at ? new Date(stats.last_import_at).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }) : ""} testid="kpi-last-import" />
      </div>

      {/* Filters */}
      <div className="mt-10 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search imports…"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/40 focus:border-orange-600"
            data-testid="imports-search-input"
          />
        </div>
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-sm rounded-lg px-3 py-2 text-zinc-200" data-testid="imports-platform-filter">
          <option value="">All platforms</option>
          {Object.entries(PLATFORM_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-sm rounded-lg px-3 py-2 text-zinc-200" data-testid="imports-status-filter">
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-zinc-900 border border-zinc-800 text-sm rounded-lg px-3 py-2 text-zinc-200" data-testid="imports-sort">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="platform">Platform</option>
          <option value="records">Most records</option>
          <option value="duration">Longest</option>
        </select>
      </div>

      {/* List */}
      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : imports.length === 0 ? (
          <div className="p-16 text-center" data-testid="imports-empty-state">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-orange-600/10 border border-orange-600/20 flex items-center justify-center mb-4">
              <ShoppingBag className="h-7 w-7 text-orange-500" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg font-medium text-zinc-100" style={{ fontFamily: "var(--font-heading)" }}>Upload your order history</h3>
            <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">
              Milkath will automatically discover your top opportunities and risks. No manual mapping — most merchants finish their first import in under a minute.
            </p>
            <button
              onClick={() => setWizardOpen(true)}
              className="mt-6 inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
              data-testid="imports-empty-cta"
            >
              <Plus className="h-4 w-4" /> Import Data
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-800">
                  <th className="px-5 py-3 font-medium">Import</th>
                  <th className="px-5 py-3 font-medium">Platform</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Records</th>
                  <th className="px-5 py-3 font-medium text-right">Orders</th>
                  <th className="px-5 py-3 font-medium text-right">Customers</th>
                  <th className="px-5 py-3 font-medium text-right">Products</th>
                  <th className="px-5 py-3 font-medium">Duration</th>
                  <th className="px-5 py-3 font-medium">Created by</th>
                  <th className="px-5 py-3 font-medium">Date</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {imports.map((imp) => {
                  const pm = PLATFORM_META[imp.platform] || PLATFORM_META.generic;
                  const sm = STATUS_META[imp.status] || STATUS_META.pending;
                  const StatusIcon = sm.icon;
                  const spinning = ["pending", "uploading", "validating", "processing"].includes(imp.status);
                  return (
                    <tr key={imp.import_id} className="border-b border-zinc-800/60 hover:bg-zinc-900/60 transition-colors" data-testid={`import-row-${imp.import_id}`}>
                      <td className="px-5 py-3">
                        <div className="text-zinc-100 truncate max-w-[200px]">{imp.name}</div>
                        <div className="text-xs text-zinc-500 truncate max-w-[200px]">{imp.file_name}</div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${pm.color}`}>{pm.label}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium border ${sm.color}`}>
                          <StatusIcon className={`h-3 w-3 ${spinning ? "animate-spin" : ""}`} />
                          {sm.label}
                          {spinning && imp.progress_pct ? <span className="ml-1 text-[10px] text-zinc-400">{imp.progress_pct}%</span> : null}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums text-zinc-300">{fmtNum(imp.total_rows)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-zinc-300">{fmtNum(imp.orders_created)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-zinc-300">{fmtNum(imp.customers_created)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-zinc-300">{fmtNum(imp.products_created)}</td>
                      <td className="px-5 py-3 text-zinc-300">{fmtDur(imp.duration_ms)}</td>
                      <td className="px-5 py-3 text-zinc-300 truncate max-w-[120px]">{imp.created_by_name}</td>
                      <td className="px-5 py-3 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(imp.created_at)}</td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => setViewing(imp)} title="View" className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid={`import-view-${imp.import_id}`}><Eye className="h-3.5 w-3.5" /></button>
                          {(imp.status === "failed" || imp.status === "partial") && (
                            <button onClick={() => handleRetry(imp.import_id)} title="Retry" className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid={`import-retry-${imp.import_id}`}><RefreshCw className="h-3.5 w-3.5" /></button>
                          )}
                          {(imp.failed_rows || 0) > 0 && (
                            <button onClick={() => handleDownloadErrors(imp.import_id)} title="Error report" className="p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-zinc-800" data-testid={`import-errors-${imp.import_id}`}><Download className="h-3.5 w-3.5" /></button>
                          )}
                          <button onClick={() => setDeleteTarget({ imp })} title="Delete" className="p-1.5 rounded-md text-zinc-400 hover:text-red-400 hover:bg-zinc-800" data-testid={`import-delete-${imp.import_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Wizard */}
      {wizardOpen && (
        <ImportWizard onClose={() => { setWizardOpen(false); refresh(); }} />
      )}

      {/* Details modal */}
      {viewing && <ImportDetails imp={viewing} onClose={() => setViewing(null)} />}

      {/* Delete confirmation — offers record-only OR full data purge */}
      {deleteTarget && (
        <DeleteImportDialog
          imp={deleteTarget.imp}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={(purge) => handleDelete(deleteTarget.imp.import_id, purge)}
        />
      )}
    </div>
  );
}

function DeleteImportDialog({ imp, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const run = async (purge) => {
    setBusy(true);
    try { await onConfirm(purge); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="delete-import-dialog">
      <div className="absolute inset-0 bg-black/70" onClick={busy ? undefined : onCancel} />
      <div className="relative w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-400" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>Delete &ldquo;{imp.name}&rdquo;</h2>
            <p className="mt-1 text-xs text-zinc-500 truncate">{imp.file_name}</p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          <button
            disabled={busy}
            onClick={() => run(false)}
            data-testid="delete-import-record-btn"
            className="w-full text-left rounded-xl border border-zinc-800 bg-zinc-950/50 hover:border-zinc-700 hover:bg-zinc-900 p-4 transition-colors disabled:opacity-50"
          >
            <p className="text-sm font-semibold text-zinc-100">Delete Import Record</p>
            <p className="mt-1 text-xs text-zinc-500 leading-relaxed">
              Removes the history row only. Orders, products and customers imported by this file <span className="text-zinc-300">stay in place</span>.
            </p>
          </button>
          <button
            disabled={busy}
            onClick={() => run(true)}
            data-testid="delete-import-data-btn"
            className="w-full text-left rounded-xl border border-red-500/40 bg-red-500/5 hover:border-red-500/60 hover:bg-red-500/10 p-4 transition-colors disabled:opacity-50"
          >
            <p className="text-sm font-semibold text-red-300">Delete Imported Data</p>
            <p className="mt-1 text-xs text-zinc-400 leading-relaxed">
              Full cascade — removes all orders, order items, products, customers and categories created by this import, plus opportunities and AI briefings.
              Dashboard, BI and Analytics will refresh instantly.
            </p>
            {(imp.orders_created || 0) + (imp.products_created || 0) + (imp.customers_created || 0) > 0 && (
              <p className="mt-2 text-[11px] tabular-nums text-zinc-500">
                Will delete {imp.orders_created || 0} orders · {imp.products_created || 0} products · {imp.customers_created || 0} customers.
              </p>
            )}
          </button>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            disabled={busy}
            onClick={onCancel}
            data-testid="delete-import-cancel"
            className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportDetails({ imp, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-2xl" data-testid="import-details">
        <h2 className="text-xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>{imp.name}</h2>
        <p className="text-xs text-zinc-500 mt-1">{imp.file_name} · {(imp.file_size / 1024).toFixed(1)} KB</p>
        <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
          <Detail label="Platform" value={imp.platform} />
          <Detail label="Status" value={imp.status} />
          <Detail label="Rows" value={imp.total_rows} />
          <Detail label="Valid rows" value={imp.valid_rows} />
          <Detail label="Failed rows" value={imp.failed_rows} />
          <Detail label="Orders" value={imp.orders_created} />
          <Detail label="Customers" value={imp.customers_created} />
          <Detail label="Products" value={imp.products_created} />
          <Detail label="Duration" value={imp.duration_ms ? `${(imp.duration_ms / 1000).toFixed(2)}s` : "—"} />
          <Detail label="Strategy" value={imp.duplicate_strategy} />
        </div>
        {imp.errors?.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-zinc-100 mb-2">First errors ({imp.errors.length})</h3>
            <div className="max-h-48 overflow-y-auto scrollbar-thin rounded-lg border border-zinc-800">
              <table className="w-full text-xs">
                <thead className="bg-zinc-950 text-zinc-500">
                  <tr><th className="px-3 py-1.5 text-left">Row</th><th className="px-3 py-1.5 text-left">Reason</th></tr>
                </thead>
                <tbody>
                  {imp.errors.slice(0, 30).map((e, i) => (
                    <tr key={i} className="border-t border-zinc-800/60"><td className="px-3 py-1.5 text-zinc-300">{e.row}</td><td className="px-3 py-1.5 text-zinc-400">{e.reason}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm" data-testid="import-details-close">Close</button>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-lg px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">{label}</p>
      <p className="mt-1 text-sm text-zinc-100 capitalize">{value ?? "—"}</p>
    </div>
  );
}
